import {
  useMemo,
  RefObject,
  useLayoutEffect,
  useState,
  useEffect,
} from "react";
import { Col, Row } from "react-grid-system";
import { Channel, CustomerInfo } from "../../../lib/api/models";
import { useGetCustomerInfo } from "../../../lib/api/queries";
import {
  CMInfoScriptName,
  getCMInfoScripts,
} from "../../../lib/scripts/cmInfo/cmInfoScripts";
import ConversationScript from "../../common/ConversationScript";
import Spinner from "../../common/Spinner";
import StyledError from "../../common/StyledError";
import { CMInfoSubtool, isFormDirty, useCMInfoForm } from "../CMInfo.hooks";
import ShowScriptButton from "../ShowScriptButton";
import CMInfoBillingAddressSubtool from "./CMInfoBillingAddressSubtool";
import CMInfoContactPreferenceSubtool from "./CMInfoContactPreferenceSubtool";
import CMInfoEmailSubtool from "./CMInfoEmailSubtool";
import CMInfoIncomeSubtool from "./CMInfoIncomeSubtool";
import CMInfoLanguageSubtool from "./CMInfoLanguageSubtool";
import CMInfoP2DSubtool from "./CMInfoP2DSubtool";
import CMInfoSchoolSubtool from "./CMInfoSchoolSubtool";
import CMInfoSecuritySubtool from "./CMInfoSecuritySubtool";
import styles from "./CMInfoSubtools.module.css";
import CMInfoConfirmationModal from "../CMInfoConfirmationModal";
import { createPortal } from "react-dom";
import StyledButton from "../../common/buttons/StyledButton";
import { convertDateStringToMonthDayYearFormat } from "../../../lib/formatting";
import { ChannelMapToFull } from "../../../lib/channelUtil";

const strings = {
  customerInfoErrorTitle: "Failed to load Customer Information",
  resetAll: "Reset All",
  updateAll: "Update All",
  script: "Script",
};

const SubtoolKeyToScriptName: Record<string, string[]> = {
  [CMInfoSubtool.Language]: [CMInfoScriptName.CMInfoLanguage],
  [CMInfoSubtool.BillingAddress]: [CMInfoScriptName.CMInfoBillingAddress],
  [CMInfoSubtool.Email]: [
    CMInfoScriptName.CMInfoEmail01,
    CMInfoScriptName.CMInfoEmail02,
  ],
  [CMInfoSubtool.ContactPreference]: [CMInfoScriptName.CMInfoContactPreference],
};

const isThisSubtoolsScriptOpen = (
  subtool: CMInfoSubtool,
  currentlyOpenScriptName: CMInfoScriptName | undefined,
): boolean => {
  return (
    !!currentlyOpenScriptName &&
    SubtoolKeyToScriptName[subtool].includes(currentlyOpenScriptName)
  );
};

type RenderCMInfoSubtoolsProps = {
  dwbuid: string;
  referenceId: string | undefined;
  channel: Channel;
  onDisplayGeneralErrors: (err: unknown) => void;
  customerInfo: CustomerInfo;
  onFormIsDirtyChanged: (inProgress: boolean) => void;
  onVerifiedAddressDateChange: (value: string) => void;
  resetButtonDiv: RefObject<HTMLDivElement>;
  updateButtonDiv: RefObject<HTMLDivElement>;
  onOpenPermissionToDiscussTool: () => void;
};

function RenderCMInfoSubtools({
  dwbuid,
  referenceId,
  channel,
  onDisplayGeneralErrors,
  customerInfo,
  onFormIsDirtyChanged,
  resetButtonDiv,
  updateButtonDiv,
  onVerifiedAddressDateChange,
  onOpenPermissionToDiscussTool,
}: RenderCMInfoSubtoolsProps) {
  const {
    formState,
    formDispatch,
    validationMessages,
    currentlyUpdating,
    failedSections,
    initialFormState,
    onSubmit,
  } = useCMInfoForm(
    dwbuid,
    referenceId,
    ChannelMapToFull[channel],
    customerInfo,
    onDisplayGeneralErrors,
    onVerifiedAddressDateChange,
  );

  const [confirmationModalData, setConfirmationModalData] = useState<{
    variant: "reset" | "clear";
    onSuccess: () => void;
  }>();

  const expandSubsections = customerInfo.showAddressWarning;

  const [scriptToShow, setScriptToShow] = useState<CMInfoScriptName>();
  const [hasMounted, setHasMounted] = useState(false);

  const scriptDetails = useMemo(() => {
    if (!scriptToShow) {
      return;
    }

    return getCMInfoScripts(scriptToShow, initialFormState);
  }, [initialFormState, scriptToShow]);

  useEffect(() => {
    onFormIsDirtyChanged(isFormDirty(formState, initialFormState));
  }, [formState, initialFormState, onFormIsDirtyChanged]);

  useLayoutEffect(() => {
    if (resetButtonDiv.current && updateButtonDiv.current) {
      setHasMounted(true);
    }
  }, [resetButtonDiv, updateButtonDiv]);

  return (
    <>
      {hasMounted &&
        resetButtonDiv.current &&
        createPortal(
          <StyledButton
            className={styles.button}
            preventDefaultDisabledStyles={true}
            variant="secondary"
            onClick={() =>
              setConfirmationModalData({
                variant: "reset",
                onSuccess: () => formDispatch(initialFormState),
              })
            }
            disabled={!isFormDirty(formState, initialFormState)}
          >
            {strings.resetAll}
          </StyledButton>,
          resetButtonDiv.current,
        )}
      {hasMounted &&
        updateButtonDiv.current &&
        createPortal(
          <StyledButton
            className={styles.button}
            preventDefaultDisabledStyles={true}
            variant="primary"
            onClick={() => onSubmit()}
            isLoading={currentlyUpdating === "ALL"}
            disabled={
              !isFormDirty(formState, initialFormState) || !!currentlyUpdating
            }
          >
            {strings.updateAll}
          </StyledButton>,
          updateButtonDiv.current,
        )}
      <Row>
        <Col md={7.8} xxl={6.8} className={styles.subtoolsContainer}>
          <CMInfoSecuritySubtool
            className={styles.subtool}
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.Security]}
            initialFormState={initialFormState[CMInfoSubtool.Security]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.Security]: {
                  ...formState[CMInfoSubtool.Security],
                  ...partialPayload,
                },
              })
            }
            security={customerInfo.security}
            onUpdate={() => onSubmit(CMInfoSubtool.Security)}
            currentlyUpdating={currentlyUpdating}
          />
          <CMInfoBillingAddressSubtool
            className={styles.subtool}
            startExpanded={expandSubsections}
            isScriptOpen={isThisSubtoolsScriptOpen(
              CMInfoSubtool.BillingAddress,
              scriptToShow,
            )}
            onSetScriptOpen={(open) =>
              setScriptToShow(
                open ? CMInfoScriptName.CMInfoBillingAddress : undefined,
              )
            }
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            showAddressWarning={customerInfo.showAddressWarning}
            formState={formState[CMInfoSubtool.BillingAddress]}
            initialFormState={initialFormState[CMInfoSubtool.BillingAddress]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.BillingAddress]: {
                  ...formState[CMInfoSubtool.BillingAddress],
                  ...partialPayload,
                },
              })
            }
            validationMessages={validationMessages}
            lastChangedDate={customerInfo.billingAddress.lastAddressDateChange}
            onUpdate={() => onSubmit(CMInfoSubtool.BillingAddress)}
            currentlyUpdating={currentlyUpdating}
            failedSections={failedSections}
          />
          <CMInfoEmailSubtool
            className={styles.subtool}
            startExpanded={expandSubsections}
            isScriptOpen={isThisSubtoolsScriptOpen(
              CMInfoSubtool.Email,
              scriptToShow,
            )}
            onSetScriptOpen={(open) =>
              setScriptToShow(
                open
                  ? initialFormState[CMInfoSubtool.Email].emailOptOut
                    ? CMInfoScriptName.CMInfoEmail02
                    : CMInfoScriptName.CMInfoEmail01
                  : undefined,
              )
            }
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.Email]}
            initialFormState={initialFormState[CMInfoSubtool.Email]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.Email]: {
                  ...formState[CMInfoSubtool.Email],
                  ...partialPayload,
                },
              })
            }
            validationMessages={validationMessages}
            onUpdate={() => onSubmit(CMInfoSubtool.Email)}
            currentlyUpdating={currentlyUpdating}
            failedSections={failedSections}
          />
          <CMInfoP2DSubtool
            className={styles.subtool}
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.P2D]}
            initialFormState={initialFormState[CMInfoSubtool.P2D]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.P2D]: {
                  ...formState[CMInfoSubtool.P2D],
                  ...partialPayload,
                },
              })
            }
            onUpdate={() => onSubmit(CMInfoSubtool.P2D)}
            currentlyUpdating={currentlyUpdating}
            contacts={customerInfo.permissionToDiscuss.contacts}
            onOpenPermissionToDiscussTool={onOpenPermissionToDiscussTool}
          />
          <CMInfoIncomeSubtool
            className={styles.subtool}
            startExpanded={expandSubsections}
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.EmploymentIncome]}
            initialFormState={initialFormState[CMInfoSubtool.EmploymentIncome]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.EmploymentIncome]: {
                  ...formState[CMInfoSubtool.EmploymentIncome],
                  ...partialPayload,
                },
              })
            }
            validationMessages={validationMessages}
            onUpdate={() => onSubmit(CMInfoSubtool.EmploymentIncome)}
            currentlyUpdating={currentlyUpdating}
            failedSections={failedSections}
          />
          <CMInfoSchoolSubtool
            className={styles.subtool}
            startExpanded={expandSubsections}
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.School]}
            initialFormState={initialFormState[CMInfoSubtool.School]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                school: {
                  ...formState[CMInfoSubtool.School],
                  ...partialPayload,
                },
              })
            }
            validationMessages={validationMessages}
            onUpdate={() => onSubmit(CMInfoSubtool.School)}
            currentlyUpdating={currentlyUpdating}
            failedSections={failedSections}
          />
          <CMInfoContactPreferenceSubtool
            className={styles.subtool}
            startExpanded={expandSubsections}
            isScriptOpen={isThisSubtoolsScriptOpen(
              CMInfoSubtool.ContactPreference,
              scriptToShow,
            )}
            onSetScriptOpen={(open) =>
              setScriptToShow(
                open ? CMInfoScriptName.CMInfoContactPreference : undefined,
              )
            }
            onShowConfirmationModal={(
              variant: "reset" | "clear",
              onSuccess: () => void,
            ) =>
              setConfirmationModalData({
                variant,
                onSuccess,
              })
            }
            formState={formState[CMInfoSubtool.ContactPreference]}
            initialFormState={initialFormState[CMInfoSubtool.ContactPreference]}
            generalDispatch={(partialPayload) =>
              formDispatch({
                ...formState,
                [CMInfoSubtool.ContactPreference]: {
                  ...formState[CMInfoSubtool.ContactPreference],
                  ...partialPayload,
                },
              })
            }
            contactPreferenceOptions={customerInfo.contactPreferenceOptions}
            onUpdate={() => onSubmit(CMInfoSubtool.ContactPreference)}
            currentlyUpdating={currentlyUpdating}
            failedSections={failedSections}
            validationMessages={validationMessages}
          />
          {customerInfo.showLanguageSection ? (
            <CMInfoLanguageSubtool
              className={styles.subtool}
              isScriptOpen={isThisSubtoolsScriptOpen(
                CMInfoSubtool.Language,
                scriptToShow,
              )}
              onSetScriptOpen={(open) =>
                setScriptToShow(
                  open ? CMInfoScriptName.CMInfoLanguage : undefined,
                )
              }
              onShowConfirmationModal={(
                variant: "reset" | "clear",
                onSuccess: () => void,
              ) =>
                setConfirmationModalData({
                  variant,
                  onSuccess,
                })
              }
              formState={formState[CMInfoSubtool.Language]}
              initialFormState={initialFormState[CMInfoSubtool.Language]}
              generalDispatch={(partialPayload) =>
                formDispatch({
                  ...formState,
                  [CMInfoSubtool.Language]: {
                    ...formState[CMInfoSubtool.Language],
                    ...partialPayload,
                  },
                })
              }
              languageOptions={customerInfo.languageOptions}
              onUpdate={() => onSubmit(CMInfoSubtool.Language)}
              currentlyUpdating={currentlyUpdating}
              failedSections={failedSections}
              validationMessages={validationMessages}
            />
          ) : null}
        </Col>
        <Col offset={{ md: 0.2 }} md={4} xxl={5}>
          <div className={styles.scriptColumn}>
            <div className={styles.scriptHeader}>
              <div className={styles.scriptHeaderText}>{strings.script}</div>
              {scriptToShow ? (
                <ShowScriptButton
                  showScript={true}
                  onClick={() => setScriptToShow(undefined)}
                  className={styles.showScriptButton}
                />
              ) : null}
            </div>
            {scriptDetails ? (
              <ConversationScript scripts={scriptDetails} />
            ) : null}
          </div>
        </Col>
      </Row>
      <CMInfoConfirmationModal
        isOpen={!!confirmationModalData}
        onRequestClose={() => setConfirmationModalData(undefined)}
        variant={confirmationModalData?.variant}
        onSuccess={() => {
          confirmationModalData?.onSuccess();
          setConfirmationModalData(undefined);
        }}
      />
    </>
  );
}

type Props = {
  dwbuid: string;
  onDisplayGeneralErrors: (err: unknown) => void;
  onFormIsDirtyChanged: (inProgress: boolean) => void;
  referenceId?: string;
  channel: Channel;
  onVerifiedAddressDateChange: (value: string) => void;
  resetButtonDiv: RefObject<HTMLDivElement>;
  updateButtonDiv: RefObject<HTMLDivElement>;
  onOpenPermissionToDiscussTool: () => void;
};

export default function CMInfoSubtools({
  dwbuid,
  onDisplayGeneralErrors,
  onFormIsDirtyChanged,
  referenceId,
  channel,
  onVerifiedAddressDateChange,
  resetButtonDiv,
  updateButtonDiv,
  onOpenPermissionToDiscussTool,
}: Props) {
  const {
    data: customerInfo,
    isLoading: isGetCustomerInfoLoading,
    isError: isGetCustomerInfoError,
    error: getCustomerInfoError,
    refetch: getCustomerInfoRefetch,
  } = useGetCustomerInfo({
    dwbuid,
    referenceId,
    channel: ChannelMapToFull[channel],
  });

  useEffect(() => {
    if (
      customerInfo?.billingAddressVerifiedStatus.dueForVerification === false &&
      customerInfo?.billingAddressVerifiedStatus.date
    ) {
      onVerifiedAddressDateChange(
        convertDateStringToMonthDayYearFormat(
          customerInfo.billingAddressVerifiedStatus.date,
        ),
      );
    }
  }, [customerInfo, onVerifiedAddressDateChange]);

  return (
    <>
      {isGetCustomerInfoLoading ? (
        <Row>
          <Col>
            <Spinner />
          </Col>
        </Row>
      ) : isGetCustomerInfoError ? (
        <Row>
          <Col>
            <StyledError
              errorTitle={strings.customerInfoErrorTitle}
              error={getCustomerInfoError}
              refetch={getCustomerInfoRefetch}
            />
          </Col>
        </Row>
      ) : (
        <RenderCMInfoSubtools
          dwbuid={dwbuid}
          referenceId={referenceId}
          channel={channel}
          onDisplayGeneralErrors={onDisplayGeneralErrors}
          customerInfo={customerInfo}
          onFormIsDirtyChanged={onFormIsDirtyChanged}
          onVerifiedAddressDateChange={onVerifiedAddressDateChange}
          resetButtonDiv={resetButtonDiv}
          updateButtonDiv={updateButtonDiv}
          onOpenPermissionToDiscussTool={onOpenPermissionToDiscussTool}
        />
      )}
    </>
  );
}
